import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

const NAV_LINKS = [
  { id: "about", key: "about" },
  { id: "packages", key: "packages" },
  { id: "gallery", key: "gallery" },
  { id: "experience", key: "experience" },
  { id: "booking", key: "booking" },
];

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "top-0 bg-white/85 backdrop-blur-xl border-b border-border-soft"
          : "top-9 md:top-10 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          data-testid="navbar-brand"
          className={`font-serif text-xl md:text-2xl tracking-tight transition-colors ${
            scrolled || open ? "text-accent-dark" : "text-white"
          }`}
        >
          Click <span className="text-gold italic">Photography</span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => handleClick(l.id)}
              className={`text-[12px] tracking-[0.2em] uppercase font-medium transition-colors hover:text-gold ${
                scrolled ? "text-accent-dark" : "text-white"
              }`}
            >
              {t.nav[l.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div
            data-testid="language-toggle"
            className={`hidden sm:flex items-center text-[11px] tracking-[0.2em] font-medium border ${
              scrolled ? "border-border-soft" : "border-white/40"
            }`}
          >
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "en"
                  ? "bg-gold text-white"
                  : scrolled
                  ? "text-accent-dark"
                  : "text-white"
              }`}
            >
              EN
            </button>
            <button
              data-testid="lang-gr"
              onClick={() => setLang("gr")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "gr"
                  ? "bg-gold text-white"
                  : scrolled
                  ? "text-accent-dark"
                  : "text-white"
              }`}
            >
              GR
            </button>
          </div>

          <button
            onClick={() => handleClick("booking")}
            data-testid="navbar-cta"
            className="hidden md:inline-flex btn-luxury"
            style={{ padding: "0.65rem 1.5rem", fontSize: "0.7rem" }}
          >
            {t.nav.cta}
          </button>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className={`md:hidden p-2 ${
              scrolled || open ? "text-accent-dark" : "text-white"
            }`}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-border-soft">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => handleClick(l.id)}
                className="text-left text-sm tracking-[0.2em] uppercase text-accent-dark"
              >
                {t.nav[l.key]}
              </button>
            ))}
            <div className="flex items-center gap-3 pt-2">
              <button
                data-testid="mobile-lang-en"
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 text-xs tracking-[0.2em] border ${
                  lang === "en" ? "bg-gold text-white border-gold" : "border-border-soft"
                }`}
              >
                EN
              </button>
              <button
                data-testid="mobile-lang-gr"
                onClick={() => setLang("gr")}
                className={`px-3 py-1.5 text-xs tracking-[0.2em] border ${
                  lang === "gr" ? "bg-gold text-white border-gold" : "border-border-soft"
                }`}
              >
                GR
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
